import { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import TeacherShell, { TeacherPageHeader } from '../../../components/layout/TeacherShell';
import { Card, Button, Input, Textarea, InputLabel, InputGroup } from '../../../components/ui';
import { cn } from '../../../lib/cn';

function formatDatetimeLocal(isoString) {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export default function LessonsEdit({ course, unit, lesson, activities }) {
  const { t } = useTranslation();

  const LESSON_TYPES = [
    { id: 'reading_lesson', label: t('teacherLessons.typeReading') },
    { id: 'writing_workshop', label: t('teacherLessons.typeWriting') },
    { id: 'vocabulary', label: t('teacherLessons.typeVocabulary') },
    { id: 'grammar', label: t('teacherLessons.typeGrammar') },
    { id: 'mixed', label: t('teacherLessons.typeMixed') },
  ];
  const [formData, setFormData] = useState({
    title: lesson.title,
    description: lesson.description || '',
    lesson_type: lesson.lesson_type,
    scheduled_for: formatDatetimeLocal(lesson.scheduled_for),
    duration_minutes: lesson.duration_minutes || '',
    location: lesson.location || '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    router.patch(
      `/teacher/courses/${course.id}/units/${unit.id}/lessons/${lesson.id}`,
      { lesson: formData },
      { onFinish: () => setIsSubmitting(false) }
    );
  };

  const handleDelete = () => {
    if (confirm(t('teacherLessons.deleteConfirm'))) {
      router.delete(`/teacher/courses/${course.id}/units/${unit.id}/lessons/${lesson.id}`);
    }
  };

  const computedEndTime = () => {
    if (!formData.scheduled_for) return null;
    const start = new Date(formData.scheduled_for);
    if (isNaN(start.getTime())) return null;
    const minutes = parseInt(formData.duration_minutes, 10) || 60;
    const end = new Date(start.getTime() + minutes * 60000);
    return end.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  };

  return (
    <TeacherShell scrollTitle={<><Link href={`/teacher/courses/${course.id}/units/${unit.id}/lessons/${lesson.id}`} className="hover:text-blue-600 dark:hover:text-blue-400">{lesson.title}</Link>{`  ›  ${t('common.edit')}`}</>}>
      <TeacherPageHeader
        title={t('teacherLessons.editLesson')}
        subtitle={lesson.title}
        backLink={`/teacher/courses/${course.id}/units/${unit.id}/lessons/${lesson.id}`}
        backLabel={t('teacherLessons.backToLesson')}
      />

      <div className="max-w-2xl space-y-8">
        <Card className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <InputGroup>
              <InputLabel htmlFor="title" required>{t('teacherLessons.titleField')}</InputLabel>
              <Input id="title" value={formData.title} onChange={handleChange('title')} />
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="description">{t('teacherLessons.description')}</InputLabel>
              <Textarea id="description" value={formData.description} onChange={handleChange('description')} rows={3} />
            </InputGroup>

            <InputGroup>
              <InputLabel>{t('teacherLessons.lessonType')}</InputLabel>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {LESSON_TYPES.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, lesson_type: type.id }))}
                    className={cn(
                      'p-3 rounded-lg border-2 text-center text-sm font-medium transition-all',
                      formData.lesson_type === type.id
                        ? 'border-gray-900 bg-gray-50 dark:border-white dark:bg-gray-800 text-gray-900 dark:text-white'
                        : 'border-gray-200 hover:border-gray-300 dark:border-gray-600 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                    )}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </InputGroup>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InputGroup>
                <InputLabel htmlFor="scheduled_for">{t('teacherLessons.scheduledDateTime')}</InputLabel>
                <Input
                  id="scheduled_for"
                  type="datetime-local"
                  value={formData.scheduled_for}
                  onChange={handleChange('scheduled_for')}
                />
              </InputGroup>
              <InputGroup>
                <InputLabel htmlFor="duration_minutes">{t('teacherLessons.durationMinutes')}</InputLabel>
                <Input
                  id="duration_minutes"
                  type="number"
                  min="1"
                  value={formData.duration_minutes}
                  onChange={handleChange('duration_minutes')}
                  placeholder="60"
                />
                {computedEndTime() && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('teacherLessons.endsAt')} {computedEndTime()}
                  </p>
                )}
              </InputGroup>
            </div>

            <InputGroup>
              <InputLabel htmlFor="location">{t('teacherLessons.location')}</InputLabel>
              <Input
                id="location"
                value={formData.location}
                onChange={handleChange('location')}
                placeholder={t('teacherLessons.locationPlaceholder')}
              />
            </InputGroup>

            <div className="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button type="submit" variant="primary" disabled={isSubmitting || !formData.title.trim()}>
                {isSubmitting ? t('common.saving') : t('teacherLessons.saveChanges')}
              </Button>
              <Link href={`/teacher/courses/${course.id}/units/${unit.id}/lessons/${lesson.id}`}>
                <Button type="button" variant="ghost">{t('common.cancel')}</Button>
              </Link>
            </div>
          </form>

          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{t('teacherLessons.deleteWarning')}</p>
            <Button variant="danger" size="sm" onClick={handleDelete}>{t('teacherLessons.deleteLesson')}</Button>
          </div>
        </Card>

        {/* Assigned Activities */}
        {activities && activities.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              {t('teacherLessons.assignedActivities')} ({activities.length})
            </h3>
            <div className="space-y-2">
              {activities.map((la) => (
                <Card key={la.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Link
                        href={`/teacher/activities/${la.activity_id}/edit`}
                        className="font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400"
                      >
                        {la.title}
                      </Link>
                      <p className="text-xs text-gray-500 dark:text-gray-400 capitalize mt-0.5">
                        {la.slot?.replace('_', ' ') || 'main'} — {la.group_mode?.replace('_', ' ') || 'whole class'}{la.activity_type ? ` — ${la.activity_type.replace('_', ' ')}` : ''}
                      </p>
                    </div>
                    {la.duration_minutes && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">{la.duration_minutes} min</span>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </TeacherShell>
  );
}
